/**
 * Centralized Theme Configuration for White-Labeling
 *
 * This file contains all design tokens for the application including:
 * - Colors (brand, semantic, backgrounds, buttons, etc.)
 * - Typography
 * - Spacing
 * - Shadows
 * - Border Radius
 * - Animations
 *
 * To white-label the app, simply:
 * 1. Create a new theme in the `themes` object
 * 2. Set VITE_COLOR_THEME in your .env file
 * 3. Build the app - all colors will be applied automatically
 */

export interface ThemeConfig {
  name: string;
  colors: {
    // Brand Colors - Primary branding colors
    brand: {
      primary: string;
      primaryDark: string;
      primaryLight: string;
      primaryForeground: string;
      secondary: string;
      secondaryDark: string;
      secondaryLight: string;
      secondaryForeground: string;
      accent: string;
      accentDark: string;
      accentLight: string;
      accentForeground: string;
    };

    // Background Colors - Page and component backgrounds
    background: {
      primary: string; // Main page background (e.g., white)
      secondary: string; // Secondary background (e.g., light gray bg-gray-50)
      tertiary: string; // Tertiary background (e.g., bg-gray-100)
      elevated: string; // Elevated cards/panels (usually white)
      inverse: string; // Dark background for contrast
    };

    // Text Colors - All text colors
    text: {
      primary: string; // Main text color
      secondary: string; // Secondary text (lighter)
      tertiary: string; // Tertiary text (even lighter)
      disabled: string; // Disabled text
      inverse: string; // Text on dark backgrounds
      link: string; // Link color
      linkHover: string; // Link hover color
    };

    // Border Colors
    border: {
      primary: string; // Main border color
      secondary: string; // Secondary border
      focus: string; // Focus ring color
      error: string; // Error border
      divider: string; // Divider lines
    };

    // Button Colors
    button: {
      primary: {
        background: string;
        backgroundHover: string;
        backgroundActive: string;
        backgroundDisabled: string;
        text: string;
        textDisabled: string;
      };
      secondary: {
        background: string;
        backgroundHover: string;
        backgroundActive: string;
        border: string;
        borderHover: string;
        text: string;
        textHover: string;
      };
      ghost: {
        background: string;
        backgroundHover: string;
        backgroundActive: string;
        text: string;
      };
      danger: {
        background: string;
        backgroundHover: string;
        text: string;
      };
    };

    // Semantic Colors - Status and feedback
    semantic: {
      success: string;
      successLight: string;
      successDark: string;
      warning: string;
      warningLight: string;
      warningDark: string;
      error: string;
      errorLight: string;
      errorDark: string;
      info: string;
      infoLight: string;
      infoDark: string;
    };

    // Component-specific Colors
    components: {
      sidebar: {
        background: string;
        backgroundHover: string;
        backgroundActive: string;
        text: string;
        textActive: string;
        border: string;
      };
      header: {
        background: string;
        text: string;
        border: string;
      };
      card: {
        background: string;
        border: string;
        shadow: string;
      };
      input: {
        background: string;
        backgroundDisabled: string;
        border: string;
        borderFocus: string;
        text: string;
        placeholder: string;
      };
      modal: {
        background: string;
        overlay: string;
        border: string;
      };
    };
  };
}

// =============================================================================
// THEME DEFINITIONS
// =============================================================================

/**
 * PMX Health Theme (Default) — Design System v2.0
 * Beige canvas, soft-white surfaces, teal brand
 */
export const pmxTheme: ThemeConfig = {
  name: 'pmx',
  colors: {
    brand: {
      primary: '#00625B', // --color-brand
      primaryDark: '#003E3A', // primary teal 700 (hover)
      primaryLight: '#9DCEC6', // teal on dark backgrounds
      primaryForeground: '#FFFFFF',
      secondary: '#C9DFBB', // secondary (sage)
      secondaryDark: '#D0D5DD', // secondary stroke
      secondaryLight: '#FCFCFD', // card background
      secondaryForeground: '#152319', // text primary
      accent: '#F8EFE5', // accent fill
      accentDark: '#E8E0D6', // accent stroke
      accentLight: '#F8EFE5', // accent fill
      accentForeground: '#152319', // text primary
    },
    background: {
      primary: '#FAF6F1', // --color-bg-page
      secondary: '#FCFCFD', // --color-bg-card
      tertiary: '#F8EFE5', // accent fill
      elevated: '#FCFCFD', // --color-bg-card
      inverse: '#152319', // text primary
    },
    text: {
      primary: '#152319', // --color-text-primary
      secondary: '#3E4A42', // --color-text-secondary
      tertiary: '#838D86', // --color-text-tertiary
      disabled: '#98A2B3', // --color-text-disabled
      inverse: '#FCFCFD', // --color-text-inverse
      link: '#00625B', // brand
      linkHover: '#003E3A', // primary teal 700
    },
    border: {
      primary: '#E4E7EC', // primary stroke
      secondary: '#D0D5DD', // secondary stroke
      focus: '#00625B', // focus ring — 2px brand teal
      error: '#B42318', // error
      divider: '#E4E7EC', // primary stroke
    },
    button: {
      primary: {
        background: '#00625B', // brand
        backgroundHover: '#003E3A', // primary teal 700
        backgroundActive: '#002624', // deep teal
        backgroundDisabled: '#E4E7EC', // primary stroke
        text: '#FFFFFF',
        textDisabled: '#98A2B3', // text disabled
      },
      secondary: {
        background: '#F8EFE5', // accent fill
        backgroundHover: '#E8E0D6', // accent stroke
        backgroundActive: '#D0D5DD', // secondary stroke
        border: '#E8E0D6', // accent stroke
        borderHover: '#00625B', // brand
        text: '#3E4A42', // text secondary
        textHover: '#152319', // text primary
      },
      ghost: {
        background: 'transparent',
        backgroundHover: '#F8EFE5', // accent fill
        backgroundActive: '#E8E0D6', // accent stroke
        text: '#3E4A42', // text secondary
      },
      danger: {
        background: '#B42318', // error
        backgroundHover: '#912018',
        text: '#FFFFFF',
      },
    },
    semantic: {
      success: '#067647', // success
      successLight: '#ECFDF3', // success background
      successDark: '#085D3A',
      warning: '#B54708', // warning
      warningLight: '#FFFAEB', // warning background
      warningDark: '#93370D',
      error: '#B42318', // error
      errorLight: '#FEF3F2', // error background
      errorDark: '#912018',
      info: '#175CD3', // info
      infoLight: 'rgba(23,92,211,0.10)',
      infoDark: '#1849A9',
    },
    components: {
      sidebar: {
        background: '#FCFCFD', // card background
        backgroundHover: '#F8EFE5', // accent fill
        backgroundActive: 'rgba(0,98,91,0.12)', // teal wash
        text: '#3E4A42', // text secondary
        textActive: '#152319', // text primary
        border: '#E4E7EC', // primary stroke
      },
      header: {
        background: '#FCFCFD', // card background
        text: '#152319', // text primary
        border: '#E4E7EC', // primary stroke
      },
      card: {
        background: '#FCFCFD', // --color-bg-card
        border: '#E4E7EC', // primary stroke
        shadow: '0 1px 2px rgba(16,24,40,0.06)', // --shadow-sm
      },
      input: {
        background: '#FFFFFF',
        backgroundDisabled: '#F8EFE5', // accent fill
        border: '#D0D5DD', // stroke-default for inputs
        borderFocus: '#00625B', // brand — 2px focus
        text: '#152319', // text primary
        placeholder: '#98A2B3', // text disabled
      },
      modal: {
        background: '#FCFCFD', // card background
        overlay: 'rgba(21, 35, 25, 0.5)', // ink overlay
        border: '#E4E7EC', // primary stroke
      },
    },
  },
};

/**
 * Teal Theme
 * Modern teal theme with blue accents
 */
export const tealTheme: ThemeConfig = {
  name: 'teal',
  colors: {
    brand: {
      primary: '#14B8A6', // Teal-500
      primaryDark: '#0F766E', // Teal-700
      primaryLight: '#2DD4BF', // Teal-400
      primaryForeground: '#FFFFFF',
      secondary: '#6366F1', // Indigo-500
      secondaryDark: '#4F46E5', // Indigo-600
      secondaryLight: '#818CF8', // Indigo-400
      secondaryForeground: '#FFFFFF',
      accent: '#6366F1',
      accentDark: '#4F46E5',
      accentLight: '#818CF8',
      accentForeground: '#FFFFFF',
    },
    background: {
      primary: '#FFFFFF',
      secondary: '#F9FAFB',
      tertiary: '#F3F4F6',
      elevated: '#FFFFFF',
      inverse: '#111827',
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
      tertiary: '#6B7280',
      disabled: '#9CA3AF',
      inverse: '#FFFFFF',
      link: '#14B8A6',
      linkHover: '#0F766E',
    },
    border: {
      primary: '#E5E7EB',
      secondary: '#D1D5DB',
      focus: '#14B8A6',
      error: '#EF4444',
      divider: '#F3F4F6',
    },
    button: {
      primary: {
        background: '#111827',
        backgroundHover: '#1F2937',
        backgroundActive: '#374151',
        backgroundDisabled: '#9CA3AF',
        text: '#FFFFFF',
        textDisabled: '#D1D5DB',
      },
      secondary: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        border: '#D1D5DB',
        borderHover: '#9CA3AF',
        text: '#374151',
        textHover: '#111827',
      },
      ghost: {
        background: 'transparent',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
      },
      danger: {
        background: '#EF4444',
        backgroundHover: '#DC2626',
        text: '#FFFFFF',
      },
    },
    semantic: {
      success: '#22C55E',
      successLight: '#DCFCE7',
      successDark: '#16A34A',
      warning: '#F59E0B',
      warningLight: '#FEF3C7',
      warningDark: '#D97706',
      error: '#EF4444',
      errorLight: '#FEE2E2',
      errorDark: '#DC2626',
      info: '#3B82F6',
      infoLight: '#DBEAFE',
      infoDark: '#2563EB',
    },
    components: {
      sidebar: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
        textActive: '#111827',
        border: '#E5E7EB',
      },
      header: {
        background: '#FFFFFF',
        text: '#111827',
        border: '#E5E7EB',
      },
      card: {
        background: '#FFFFFF',
        border: '#E5E7EB',
        shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      },
      input: {
        background: '#FFFFFF',
        backgroundDisabled: '#F9FAFB',
        border: '#E5E7EB',
        borderFocus: '#14B8A6',
        text: '#111827',
        placeholder: '#9CA3AF',
      },
      modal: {
        background: '#FFFFFF',
        overlay: 'rgba(0, 0, 0, 0.5)',
        border: '#E5E7EB',
      },
    },
  },
};

/**
 * Green Theme
 * Fresh green healthcare theme
 */
export const greenTheme: ThemeConfig = {
  name: 'green',
  colors: {
    brand: {
      primary: '#22C55E',
      primaryDark: '#16A34A',
      primaryLight: '#4ADE80',
      primaryForeground: '#FFFFFF',
      secondary: '#6366F1',
      secondaryDark: '#4F46E5',
      secondaryLight: '#818CF8',
      secondaryForeground: '#FFFFFF',
      accent: '#6366F1',
      accentDark: '#4F46E5',
      accentLight: '#818CF8',
      accentForeground: '#FFFFFF',
    },
    background: {
      primary: '#FFFFFF',
      secondary: '#F9FAFB',
      tertiary: '#F3F4F6',
      elevated: '#FFFFFF',
      inverse: '#111827',
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
      tertiary: '#6B7280',
      disabled: '#9CA3AF',
      inverse: '#FFFFFF',
      link: '#22C55E',
      linkHover: '#16A34A',
    },
    border: {
      primary: '#E5E7EB',
      secondary: '#D1D5DB',
      focus: '#22C55E',
      error: '#EF4444',
      divider: '#F3F4F6',
    },
    button: {
      primary: {
        background: '#111827',
        backgroundHover: '#1F2937',
        backgroundActive: '#374151',
        backgroundDisabled: '#9CA3AF',
        text: '#FFFFFF',
        textDisabled: '#D1D5DB',
      },
      secondary: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        border: '#D1D5DB',
        borderHover: '#9CA3AF',
        text: '#374151',
        textHover: '#111827',
      },
      ghost: {
        background: 'transparent',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
      },
      danger: {
        background: '#EF4444',
        backgroundHover: '#DC2626',
        text: '#FFFFFF',
      },
    },
    semantic: {
      success: '#22C55E',
      successLight: '#DCFCE7',
      successDark: '#16A34A',
      warning: '#F59E0B',
      warningLight: '#FEF3C7',
      warningDark: '#D97706',
      error: '#EF4444',
      errorLight: '#FEE2E2',
      errorDark: '#DC2626',
      info: '#3B82F6',
      infoLight: '#DBEAFE',
      infoDark: '#2563EB',
    },
    components: {
      sidebar: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
        textActive: '#111827',
        border: '#E5E7EB',
      },
      header: {
        background: '#FFFFFF',
        text: '#111827',
        border: '#E5E7EB',
      },
      card: {
        background: '#FFFFFF',
        border: '#E5E7EB',
        shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      },
      input: {
        background: '#FFFFFF',
        backgroundDisabled: '#F9FAFB',
        border: '#E5E7EB',
        borderFocus: '#22C55E',
        text: '#111827',
        placeholder: '#9CA3AF',
      },
      modal: {
        background: '#FFFFFF',
        overlay: 'rgba(0, 0, 0, 0.5)',
        border: '#E5E7EB',
      },
    },
  },
};

/**
 * Blue Theme
 * Professional blue medical theme
 */
export const blueTheme: ThemeConfig = {
  name: 'blue',
  colors: {
    brand: {
      primary: '#3B82F6',
      primaryDark: '#2563EB',
      primaryLight: '#60A5FA',
      primaryForeground: '#FFFFFF',
      secondary: '#DB2777',
      secondaryDark: '#BE185D',
      secondaryLight: '#F472B6',
      secondaryForeground: '#FFFFFF',
      accent: '#DB2777',
      accentDark: '#BE185D',
      accentLight: '#F472B6',
      accentForeground: '#FFFFFF',
    },
    background: {
      primary: '#FFFFFF',
      secondary: '#F9FAFB',
      tertiary: '#F3F4F6',
      elevated: '#FFFFFF',
      inverse: '#111827',
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
      tertiary: '#6B7280',
      disabled: '#9CA3AF',
      inverse: '#FFFFFF',
      link: '#3B82F6',
      linkHover: '#2563EB',
    },
    border: {
      primary: '#E5E7EB',
      secondary: '#D1D5DB',
      focus: '#3B82F6',
      error: '#EF4444',
      divider: '#F3F4F6',
    },
    button: {
      primary: {
        background: '#111827',
        backgroundHover: '#1F2937',
        backgroundActive: '#374151',
        backgroundDisabled: '#9CA3AF',
        text: '#FFFFFF',
        textDisabled: '#D1D5DB',
      },
      secondary: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        border: '#D1D5DB',
        borderHover: '#9CA3AF',
        text: '#374151',
        textHover: '#111827',
      },
      ghost: {
        background: 'transparent',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
      },
      danger: {
        background: '#EF4444',
        backgroundHover: '#DC2626',
        text: '#FFFFFF',
      },
    },
    semantic: {
      success: '#22C55E',
      successLight: '#DCFCE7',
      successDark: '#16A34A',
      warning: '#F59E0B',
      warningLight: '#FEF3C7',
      warningDark: '#D97706',
      error: '#EF4444',
      errorLight: '#FEE2E2',
      errorDark: '#DC2626',
      info: '#3B82F6',
      infoLight: '#DBEAFE',
      infoDark: '#2563EB',
    },
    components: {
      sidebar: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
        textActive: '#111827',
        border: '#E5E7EB',
      },
      header: {
        background: '#FFFFFF',
        text: '#111827',
        border: '#E5E7EB',
      },
      card: {
        background: '#FFFFFF',
        border: '#E5E7EB',
        shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      },
      input: {
        background: '#FFFFFF',
        backgroundDisabled: '#F9FAFB',
        border: '#E5E7EB',
        borderFocus: '#3B82F6',
        text: '#111827',
        placeholder: '#9CA3AF',
      },
      modal: {
        background: '#FFFFFF',
        overlay: 'rgba(0, 0, 0, 0.5)',
        border: '#E5E7EB',
      },
    },
  },
};

/**
 * Purple Theme
 * Wellness and mindfulness theme
 */
export const purpleTheme: ThemeConfig = {
  name: 'purple',
  colors: {
    brand: {
      primary: '#7C3AED',
      primaryDark: '#6D28D9',
      primaryLight: '#8B5CF6',
      primaryForeground: '#FFFFFF',
      secondary: '#FBBF24',
      secondaryDark: '#F59E0B',
      secondaryLight: '#FCD34D',
      secondaryForeground: '#000000',
      accent: '#FBBF24',
      accentDark: '#F59E0B',
      accentLight: '#FCD34D',
      accentForeground: '#000000',
    },
    background: {
      primary: '#FFFFFF',
      secondary: '#F9FAFB',
      tertiary: '#F3F4F6',
      elevated: '#FFFFFF',
      inverse: '#111827',
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
      tertiary: '#6B7280',
      disabled: '#9CA3AF',
      inverse: '#FFFFFF',
      link: '#7C3AED',
      linkHover: '#6D28D9',
    },
    border: {
      primary: '#E5E7EB',
      secondary: '#D1D5DB',
      focus: '#7C3AED',
      error: '#EF4444',
      divider: '#F3F4F6',
    },
    button: {
      primary: {
        background: '#111827',
        backgroundHover: '#1F2937',
        backgroundActive: '#374151',
        backgroundDisabled: '#9CA3AF',
        text: '#FFFFFF',
        textDisabled: '#D1D5DB',
      },
      secondary: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        border: '#D1D5DB',
        borderHover: '#9CA3AF',
        text: '#374151',
        textHover: '#111827',
      },
      ghost: {
        background: 'transparent',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
      },
      danger: {
        background: '#EF4444',
        backgroundHover: '#DC2626',
        text: '#FFFFFF',
      },
    },
    semantic: {
      success: '#22C55E',
      successLight: '#DCFCE7',
      successDark: '#16A34A',
      warning: '#F59E0B',
      warningLight: '#FEF3C7',
      warningDark: '#D97706',
      error: '#EF4444',
      errorLight: '#FEE2E2',
      errorDark: '#DC2626',
      info: '#3B82F6',
      infoLight: '#DBEAFE',
      infoDark: '#2563EB',
    },
    components: {
      sidebar: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
        textActive: '#111827',
        border: '#E5E7EB',
      },
      header: {
        background: '#FFFFFF',
        text: '#111827',
        border: '#E5E7EB',
      },
      card: {
        background: '#FFFFFF',
        border: '#E5E7EB',
        shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      },
      input: {
        background: '#FFFFFF',
        backgroundDisabled: '#F9FAFB',
        border: '#E5E7EB',
        borderFocus: '#7C3AED',
        text: '#111827',
        placeholder: '#9CA3AF',
      },
      modal: {
        background: '#FFFFFF',
        overlay: 'rgba(0, 0, 0, 0.5)',
        border: '#E5E7EB',
      },
    },
  },
};

/**
 * Slate Theme
 * Professional corporate theme
 */
export const slateTheme: ThemeConfig = {
  name: 'slate',
  colors: {
    brand: {
      primary: '#475569',
      primaryDark: '#334155',
      primaryLight: '#64748B',
      primaryForeground: '#FFFFFF',
      secondary: '#3B82F6',
      secondaryDark: '#2563EB',
      secondaryLight: '#60A5FA',
      secondaryForeground: '#FFFFFF',
      accent: '#3B82F6',
      accentDark: '#2563EB',
      accentLight: '#60A5FA',
      accentForeground: '#FFFFFF',
    },
    background: {
      primary: '#FFFFFF',
      secondary: '#F9FAFB',
      tertiary: '#F3F4F6',
      elevated: '#FFFFFF',
      inverse: '#111827',
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
      tertiary: '#6B7280',
      disabled: '#9CA3AF',
      inverse: '#FFFFFF',
      link: '#475569',
      linkHover: '#334155',
    },
    border: {
      primary: '#E5E7EB',
      secondary: '#D1D5DB',
      focus: '#475569',
      error: '#EF4444',
      divider: '#F3F4F6',
    },
    button: {
      primary: {
        background: '#111827',
        backgroundHover: '#1F2937',
        backgroundActive: '#374151',
        backgroundDisabled: '#9CA3AF',
        text: '#FFFFFF',
        textDisabled: '#D1D5DB',
      },
      secondary: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        border: '#D1D5DB',
        borderHover: '#9CA3AF',
        text: '#374151',
        textHover: '#111827',
      },
      ghost: {
        background: 'transparent',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
      },
      danger: {
        background: '#EF4444',
        backgroundHover: '#DC2626',
        text: '#FFFFFF',
      },
    },
    semantic: {
      success: '#22C55E',
      successLight: '#DCFCE7',
      successDark: '#16A34A',
      warning: '#F59E0B',
      warningLight: '#FEF3C7',
      warningDark: '#D97706',
      error: '#EF4444',
      errorLight: '#FEE2E2',
      errorDark: '#DC2626',
      info: '#3B82F6',
      infoLight: '#DBEAFE',
      infoDark: '#2563EB',
    },
    components: {
      sidebar: {
        background: '#FFFFFF',
        backgroundHover: '#F9FAFB',
        backgroundActive: '#F3F4F6',
        text: '#4B5563',
        textActive: '#111827',
        border: '#E5E7EB',
      },
      header: {
        background: '#FFFFFF',
        text: '#111827',
        border: '#E5E7EB',
      },
      card: {
        background: '#FFFFFF',
        border: '#E5E7EB',
        shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      },
      input: {
        background: '#FFFFFF',
        backgroundDisabled: '#F9FAFB',
        border: '#E5E7EB',
        borderFocus: '#475569',
        text: '#111827',
        placeholder: '#9CA3AF',
      },
      modal: {
        background: '#FFFFFF',
        overlay: 'rgba(0, 0, 0, 0.5)',
        border: '#E5E7EB',
      },
    },
  },
};

// =============================================================================
// THEME REGISTRY
// =============================================================================

export const themes = {
  pmx: pmxTheme,
  teal: tealTheme,
  green: greenTheme,
  blue: blueTheme,
  purple: purpleTheme,
  slate: slateTheme,
} as const;

export type ThemeName = keyof typeof themes;

// =============================================================================
// THEME UTILITIES
// =============================================================================

/**
 * Get the current theme based on environment variable
 */
export function getTheme(): ThemeConfig {
  const themeName = (import.meta.env.VITE_COLOR_THEME || 'pmx') as ThemeName;
  return themes[themeName] || themes.pmx;
}

/**
 * Generate CSS variables from theme config
 * These variables can be used in Tailwind config and components
 */
export function generateCSSVariables(theme: ThemeConfig): Record<string, string> {
  const { colors } = theme;

  return {
    // Brand colors
    '--color-brand-primary': colors.brand.primary,
    '--color-brand-primary-dark': colors.brand.primaryDark,
    '--color-brand-primary-light': colors.brand.primaryLight,
    '--color-brand-primary-foreground': colors.brand.primaryForeground,
    '--color-brand-secondary': colors.brand.secondary,
    '--color-brand-secondary-dark': colors.brand.secondaryDark,
    '--color-brand-secondary-light': colors.brand.secondaryLight,
    '--color-brand-secondary-foreground': colors.brand.secondaryForeground,
    '--color-brand-accent': colors.brand.accent,
    '--color-brand-accent-dark': colors.brand.accentDark,
    '--color-brand-accent-light': colors.brand.accentLight,
    '--color-brand-accent-foreground': colors.brand.accentForeground,

    // Background colors
    '--color-bg-primary': colors.background.primary,
    '--color-bg-secondary': colors.background.secondary,
    '--color-bg-tertiary': colors.background.tertiary,
    '--color-bg-elevated': colors.background.elevated,
    '--color-bg-inverse': colors.background.inverse,

    // Text colors
    '--color-text-primary': colors.text.primary,
    '--color-text-secondary': colors.text.secondary,
    '--color-text-tertiary': colors.text.tertiary,
    '--color-text-disabled': colors.text.disabled,
    '--color-text-inverse': colors.text.inverse,
    '--color-text-link': colors.text.link,
    '--color-text-link-hover': colors.text.linkHover,

    // Border colors
    '--color-border-primary': colors.border.primary,
    '--color-border-secondary': colors.border.secondary,
    '--color-border-focus': colors.border.focus,
    '--color-border-error': colors.border.error,
    '--color-border-divider': colors.border.divider,

    // Button colors
    '--color-btn-primary-bg': colors.button.primary.background,
    '--color-btn-primary-bg-hover': colors.button.primary.backgroundHover,
    '--color-btn-primary-bg-active': colors.button.primary.backgroundActive,
    '--color-btn-primary-bg-disabled': colors.button.primary.backgroundDisabled,
    '--color-btn-primary-text': colors.button.primary.text,
    '--color-btn-primary-text-disabled': colors.button.primary.textDisabled,
    '--color-btn-secondary-bg': colors.button.secondary.background,
    '--color-btn-secondary-bg-hover': colors.button.secondary.backgroundHover,
    '--color-btn-secondary-bg-active': colors.button.secondary.backgroundActive,
    '--color-btn-secondary-border': colors.button.secondary.border,
    '--color-btn-secondary-border-hover': colors.button.secondary.borderHover,
    '--color-btn-secondary-text': colors.button.secondary.text,
    '--color-btn-secondary-text-hover': colors.button.secondary.textHover,

    // Semantic colors
    '--color-success': colors.semantic.success,
    '--color-success-light': colors.semantic.successLight,
    '--color-success-dark': colors.semantic.successDark,
    '--color-warning': colors.semantic.warning,
    '--color-warning-light': colors.semantic.warningLight,
    '--color-warning-dark': colors.semantic.warningDark,
    '--color-error': colors.semantic.error,
    '--color-error-light': colors.semantic.errorLight,
    '--color-error-dark': colors.semantic.errorDark,
    '--color-info': colors.semantic.info,
    '--color-info-light': colors.semantic.infoLight,
    '--color-info-dark': colors.semantic.infoDark,

    // Component colors
    '--color-sidebar-bg': colors.components.sidebar.background,
    '--color-sidebar-bg-hover': colors.components.sidebar.backgroundHover,
    '--color-sidebar-bg-active': colors.components.sidebar.backgroundActive,
    '--color-sidebar-text': colors.components.sidebar.text,
    '--color-sidebar-text-active': colors.components.sidebar.textActive,
    '--color-sidebar-border': colors.components.sidebar.border,
    '--color-header-bg': colors.components.header.background,
    '--color-header-text': colors.components.header.text,
    '--color-header-border': colors.components.header.border,
    '--color-card-bg': colors.components.card.background,
    '--color-card-border': colors.components.card.border,
    '--color-input-bg': colors.components.input.background,
    '--color-input-bg-disabled': colors.components.input.backgroundDisabled,
    '--color-input-border': colors.components.input.border,
    '--color-input-border-focus': colors.components.input.borderFocus,
    '--color-input-text': colors.components.input.text,
    '--color-input-placeholder': colors.components.input.placeholder,
    '--color-modal-bg': colors.components.modal.background,
    '--color-modal-overlay': colors.components.modal.overlay,
    '--color-modal-border': colors.components.modal.border,
  };
}

/**
 * Convert hex color to HSL format for shadcn/ui
 * Example: #00625B -> "177 100% 19%"
 */
function hexToHSL(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
}

export function generateShadcnVariables(theme: ThemeConfig): Record<string, string> {
  const { colors } = theme;
  const semanticColors: Record<string, string> = {
    background: colors.background.primary,
    foreground: colors.text.primary,
    card: colors.components.card.background,
    'card-foreground': colors.text.primary,
    popover: colors.components.modal.background,
    'popover-foreground': colors.text.primary,
    primary: colors.button.primary.background,
    'primary-dark': colors.button.primary.backgroundHover,
    'primary-light': colors.brand.primaryLight,
    'primary-foreground': colors.button.primary.text,
    secondary: colors.button.secondary.background,
    'secondary-foreground': colors.button.secondary.text,
    muted: colors.background.tertiary,
    'muted-foreground': colors.text.secondary,
    accent: colors.brand.accent,
    'accent-dark': colors.brand.accentDark,
    'accent-light': colors.brand.accentLight,
    'accent-foreground': colors.brand.accentForeground,
    success: colors.semantic.success,
    warning: colors.semantic.warning,
    error: colors.semantic.error,
    info: colors.semantic.info,
    destructive: colors.semantic.error,
    'destructive-foreground': colors.button.danger.text,
    border: colors.border.primary,
    input: colors.components.input.border,
    ring: colors.border.focus,
  };

  return Object.fromEntries(
    Object.entries(semanticColors).map(([name, color]) => [`--theme-${name}`, hexToHSL(color)])
  );
}

export function applyCSSVariables(theme: ThemeConfig): void {
  const variables = { ...generateCSSVariables(theme), ...generateShadcnVariables(theme) };
  const root = document.documentElement;

  // Apply theme-specific CSS variables (--color-brand-primary, etc.)
  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  // Sidebar overrides from env vars
  const sidebarBgColor = import.meta.env.VITE_SIDEBAR_BG_COLOR as string;
  const sidebarStyle =
    (import.meta.env.VITE_SIDEBAR_STYLE as string) === 'light' ? 'light' : 'dark';
  const resolvedSidebarBg =
    sidebarBgColor && sidebarBgColor.startsWith('#')
      ? sidebarBgColor
      : sidebarStyle === 'light'
        ? '#FCFCFD'
        : '#152319';
  root.style.setProperty('--sidebar-bg', resolvedSidebarBg);
}

/**
 * Initialize theme on app startup
 * Call this in your main app entry point
 */
export function initializeTheme(): void {
  const theme = getTheme();
  applyCSSVariables(theme);
  console.log(`✅ Theme initialized: ${theme.name}`);
}
